import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FiCalendar } from "react-icons/fi";
import { IoArrowBack } from "react-icons/io5";
import { tableOrderService } from "../../../api/services/table-order.service";
import MobileViewContainer from "../../../Components/MobileViewContainer";
import useAutoLoadMore from "../../../hooks/useAutoLoadMore";
import {
  Card,
  CardHeader,
  StatusBadge,
  StatusDot,
  OrderInfo,
  OrderId,
  OrderDate,
  ProductName,
  DateInfo,
  SeatBadge,
  ActionButtons,
  DetailButton,
  ContentArea,
} from "../../../Components/Common";
import { LoadingSpinner, EmptyMessage, FilterBadges } from "../../../Components/Common";
import { getStatusText } from "../../../utils/statusUtils";
import { formatDate, formatOrderId } from "../../../utils/formatters";

const FILTER_OPTIONS = ["전체", "진행중", "완료"];

const STATUS_BADGE: Record<string, { status: string; label: string }> = {
  NEW: { status: "주문접수", label: "주문접수" },
  ACCEPTED: { status: "결제완료", label: "접수완료" },
  SERVED: { status: "완료", label: "서빙완료" },
  CANCELED: { status: "취소완료", label: "취소완료" },
};

const MANUFACTURING_STATUS_BADGE: Record<string, { status: string; label: string }> = {
  NEW: { status: "주문접수", label: "제조 대기" },
  ACCEPTED: { status: "결제완료", label: "제조중" },
  SERVED: { status: "완료", label: "제조완료" },
  CANCELED: { status: "취소완료", label: "취소완료" },
};

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const shouldOpenLiveStatus = (order: any) =>
  order.fulfillment_type === "KITCHEN" &&
  (order.status === "NEW" || order.status === "ACCEPTED");

const getStatusBadge = (order: any) => {
  const source = order.fulfillment_type === "KITCHEN" ? MANUFACTURING_STATUS_BADGE : STATUS_BADGE;
  return source[order.status] ?? {
    status: order.status,
    label: order.status,
  };
};

export default function TableOrderList() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("전체");

  const query = useInfiniteQuery({
    queryKey: ["table-order-list"],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await tableOrderService.listMyOrders({ page: pageParam });
      return res.data;
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.next) {
        const match = lastPage.next.match(/page=(\d+)/);
        if (match) return match[1];
      }
      return undefined;
    },
  });

  const loadMoreRef = useAutoLoadMore({
    enabled: true,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    onLoadMore: query.fetchNextPage,
    rootMargin: "160px 0px",
  });

  const allResults: any[] = (query.data?.pages.flatMap((p: any) => p.results) ?? [])
    .filter((order: any) => {
      if (filter === "진행중") return ["NEW", "ACCEPTED"].includes(order.status);
      if (filter === "완료") return ["SERVED", "CANCELED"].includes(order.status);
      return true;
    })
    .sort((left: any, right: any) => {
      const leftPriority = shouldOpenLiveStatus(left) ? 0 : 1;
      const rightPriority = shouldOpenLiveStatus(right) ? 0 : 1;
      return leftPriority - rightPriority;
    });

  return (
    <MobileViewContainer>
      <div className="min-h-screen bg-gray-50 pb-24">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-white px-4 py-3 shadow-[0_1px_0_#eee]">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100"
          >
            <IoArrowBack size={20} />
          </button>
          <h1 className="text-base font-semibold" style={{ fontFamily: "Gmarket-M" }}>
            오더 내역
          </h1>
        </div>

        <FilterBadges options={FILTER_OPTIONS} activeFilter={filter} onFilterChange={setFilter} />

        <ContentArea>
          {query.isLoading ? (
            <LoadingSpinner height="200px" />
          ) : allResults.length === 0 ? (
            <EmptyMessage message="주문 내역이 없습니다." />
          ) : (
            <>
              {allResults.map((order: any) => {
                const badge = getStatusBadge(order);
                const tableLabel =
                  order.table_name && order.table_code
                    ? `${order.table_name} (${order.table_code})`
                    : order.order_type === "pickup"
                      ? "픽업 주문"
                      : "테이블 미지정";
                const orderTypeLabel = order.order_type === "pickup" ? "픽업" : "서빙";
                const openLiveStatus = shouldOpenLiveStatus(order);

                return (
                  <Card key={order.id}>
                    <CardHeader>
                      <StatusBadge status={badge.status}>
                        <StatusDot status={badge.status} />
                        {badge.label}
                      </StatusBadge>
                      <OrderInfo>
                        <OrderId>{formatOrderId(order.id, 8, "O-")}</OrderId>
                        <OrderDate>{formatDate(order.created_at)}</OrderDate>
                      </OrderInfo>
                    </CardHeader>

                    <ProductName>
                      {order.items?.map((item: any) => item.menu_name_snapshot).join(", ") ||
                        "주문 메뉴"}
                    </ProductName>

                    <DateInfo>
                      <FiCalendar size={14} />
                      {tableLabel}
                      <span style={{ margin: "0 4px" }}>•</span>
                      {orderTypeLabel}
                      {order.fulfillment_type ? (
                        <>
                          <span style={{ margin: "0 4px" }}>•</span>
                          {fulfillmentTypeLabels[order.fulfillment_type] || order.fulfillment_type}
                        </>
                      ) : null}
                    </DateInfo>

                    {order.customer_phone_num && (
                      <SeatBadge>
                        {order.customer_name || "전화번호 연결 주문"}
                      </SeatBadge>
                    )}

                    <ActionButtons>
                      <DetailButton
                        onClick={() => navigate(`/my-page/table-order/${order.id}`)}
                      >
                        {openLiveStatus ? "주문 현황보기" : "주문내역 보기"}
                      </DetailButton>
                    </ActionButtons>
                  </Card>
                );
              })}

              {(query.hasNextPage || query.isFetchingNextPage) && (
                <div
                  ref={loadMoreRef}
                  className="flex justify-center py-4 text-sm text-gray-500"
                  style={{ minHeight: 48 }}
                >
                  {query.isFetchingNextPage
                    ? "주문 내역을 더 불러오는 중..."
                    : "아래로 스크롤하면 더 불러옵니다."}
                </div>
              )}
            </>
          )}
        </ContentArea>
      </div>
    </MobileViewContainer>
  );
}
